import { useMeQuery } from '@shared/api';

/**
 * Коды прав-действий. Источник истины — бэкенд (`osnova_core::domain::actions`,
 * миграция 0301): право на агента отвечает «видит и пишет», действие — «может
 * ли сделать вот это».
 */
export type ActionCode =
  | 'econ.bdr.approve'
  | 'econ.prc.approve'
  | 'econ.payroll.read'
  | 'econ.cash.assign'
  | 'fleet.write'
  | 'smeta.fgiscs.restore_archived'
  | 'mto.receipts.write'
  | 'mto.receipts.map';

export interface ActionPermission {
  /** Действие есть хотя бы у одной роли пользователя. */
  allowed: boolean;
  /** Действие можно применять к своему (утвердить то, что сам загрузил). */
  allowOwn: boolean;
  /** Текущий пользователь — чтобы сравнить с автором записи. */
  userId: string | undefined;
}

/** Почему действие недоступно: нет права или запись своя. */
export type ActionRefusal = 'missing' | 'own';

/**
 * Право-действие текущего пользователя. Пока `/auth/me` грузится — действия
 * нет: лучше погасить кнопку, чем получить 403. Бэкенд всё равно проверяет
 * сам (`require_action` в `osnova-api/src/extractors.rs`).
 */
export function useActionPermission(action: ActionCode): ActionPermission {
  const { data } = useMeQuery();
  const grant = data?.actions?.find((a) => a.action === action);
  return {
    allowed: Boolean(grant),
    allowOwn: Boolean(grant?.allow_own),
    userId: data?.user.id,
  };
}

/**
 * Можно ли применить действие к записи автора `authorId`: правило «автор ≠
 * утверждающий» (ТЗ п. 5.2, принцип 2), если роль не разрешает своё.
 * `authorId` пустой — автора нет, хватает самого действия.
 */
export function actionRefusal(
  permission: ActionPermission,
  authorId?: string | null,
): ActionRefusal | null {
  if (!permission.allowed) return 'missing';
  if (authorId && permission.userId === authorId && !permission.allowOwn) return 'own';
  return null;
}
